import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Image } from '@/libraries/design/components/image/src/image';
import { ColumnLayout, wacToken } from '@/libraries/design/foundations';
import { RichTextComponent } from '@/libraries/design/utils/rich-text/src/rich-text';

export interface SpeakerProps {
	name: string;
	photoSrc: string;
	jobTitle: string;
	company?: string;
	bio?: React.ReactElement<typeof RichTextComponent>;
}

const Speaker = ({
	name,
	photoSrc,
	jobTitle,
	company,
	bio,
	size,
}: SpeakerProps & { size: 'large' | 'small' }) => {
	const nameAndTitle = (showOnlyOnMobile: boolean) => {
		return (
			<div
				css={[nameAndTitleStyles, showOnlyOnMobile ? showOnlyOnMobileStyles : hideOnMobileStyles]}
			>
				<p className="card-title-sm" css={noMarginStyles}>
					{name}
				</p>
				<p className="body-sm" css={noMarginStyles}>
					{jobTitle}
					{company && `, ${company}`}
				</p>
			</div>
		);
	};
	return (
		<li css={speakerStyles}>
			<div css={imageContainerStyles}>
				<Image
					src={photoSrc}
					alt=""
					css={[
						imageStyles,
						size === 'large' && largeImageStyles,
						size === 'small' && smallImageStyles,
					]}
				/>
				{nameAndTitle(true)}
			</div>
			<div css={textContainerStyles}>
				{nameAndTitle(false)}
				<div className="body-md">{bio}</div>
			</div>
		</li>
	);
};

export interface SpeakersProps {
	speakers: SpeakerProps[];
	title?: string;
}

export const Speakers = ({ speakers, title }: SpeakersProps) => {
	const intl = useIntl();
	const defaultTitleSingleSpeaker = intl.formatMessage({
		id: 'speakers.defaultTitle.single',
		defaultMessage: 'More about the speaker',
	});
	const defaultTitleMultipleSpeakers = intl.formatMessage({
		id: 'speakers.defaultTitle.multiple',
		defaultMessage: 'More about the speakers',
	});
	return (
		<section css={sectionStyles}>
			<ColumnLayout
				layout={speakers.length === 1 ? '8' : speakers.length === 2 ? '10' : '12'}
				tabletLayout="12"
			>
				<div css={mainContainerStyles}>
					<h2 className="eyebrow-md" css={[titleStyles, noMarginStyles]}>
						{title ??
							(speakers.length === 1 ? defaultTitleSingleSpeaker : defaultTitleMultipleSpeakers)}
					</h2>
					<ul
						// Adding role="list" is prudent when using list-style: none, because some browser/screen-reader combinations have historically stopped announcing an unstyled <ul> as a list.
						role="list"
						css={[
							speakersContainerStyles,
							(speakers.length === 5 || speakers.length === 6) && fiveOrSixSpeakersContainerStyles,
							speakers.length === 7 && sevenSpeakersContainerStyles,
						]}
					>
						{speakers.map((speakerProps, index) => (
							<Speaker
								key={index}
								{...speakerProps}
								size={speakers.length < 4 ? 'large' : 'small'}
							/>
						))}
					</ul>
				</div>
			</ColumnLayout>
		</section>
	);
};

const speakerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	[media.only.sm]: {
		flexDirection: 'row',
	},
	gap: token('space.200'),
	[media.below.sm]: {
		gap: token('space.100'),
	},
});

const imageContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
	[media.below.sm]: {
		flexDirection: 'row',
		alignItems: 'center',
	},
});

const imageStyles = css({ borderRadius: '50%' });

const largeImageStyles = css({
	height: '120px',
	width: '120px',
});

const smallImageStyles = css({
	height: '88px',
	width: '88px',
});

const textContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.250'),
	[media.only.sm]: {
		marginBlockStart: token('space.200'),
	},
});

const nameAndTitleStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
	[media.only.sm]: {
		gap: token('space.025'),
	},
	[media.below.sm]: {
		gap: 0,
	},
});

const showOnlyOnMobileStyles = css({
	[media.above.sm]: {
		display: 'none',
	},
});

const hideOnMobileStyles = css({
	[media.below.sm]: {
		display: 'none',
	},
});

const sectionStyles = css({
	paddingBlockStart: token('space.400'),
	paddingBlockEnd: token('space.1000'),
	[media.below.sm]: {
		backgroundColor: wacToken('color.extended.blue100'),
	},
});

const mainContainerStyles = css({
	backgroundColor: wacToken('color.extended.blue100'),
	display: 'flex',
	flexDirection: 'column',
	padding: token('space.800'),
	paddingBlockStart: token('space.400'),
	gap: token('space.400'),
	[media.only.sm]: {
		paddingInline: token('space.500'),
	},
	[media.below.sm]: {
		paddingInline: 0,
	},
});

const titleStyles = css({
	textAlign: 'center',
});

const speakersContainerStyles = css({
	margin: 0,
	padding: 0,
	listStyle: 'none',
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fit, minmax(146px, 1fr))',
	gap: token('space.800'),
	[media.below.md]: {
		gridTemplateColumns: '1fr',
	},
});

const fiveOrSixSpeakersContainerStyles = css({
	gridTemplateColumns: 'repeat(3, 1fr)',
});

const sevenSpeakersContainerStyles = css({
	gridTemplateColumns: 'repeat(4, 1fr)',
});

const noMarginStyles = css({
	margin: 0,
});
